import { useMemo } from 'react'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    FilterBar,
    DataTableAction,
} from '@/components/ui/data-table-customized'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import CheckIcons from '@/icons/CheckIcons'
import CancelIcons from '@/icons/CancelIcons'
import { confirmService, rejectedService } from '@/modules/Service/services'
import { Service } from '@/modules/Service/types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useServiceList } from '@/modules/Service/hooks'
import { POC_SERVICE_STATUSES } from '@/modules/Service/utils/service-list-config'

const POC = () => {
    const { refetch } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })

    const { setConfirmation } = useConfirmation()
    const { setModal } = useModal()

    const {
        services,
        pagination,
        stateComponent,
        isFetching,
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        filterItems,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    } = useServiceList({
        type: 'poc',
        serviceStatuses: POC_SERVICE_STATUSES,
        showAccountManager: true,
        showDateFilters: false,
    })

    // Mutations
    const { mutateAsync: confirm } = useMutationWithHandler(
        { mutationFn: confirmService },
        {
            reactQueryKeys: QUERY_KEYS.SERVICE,
            successMessage: t('confirm-service-success'),
            onSuccess: () => refetch(),
        }
    )

    const { mutateAsync: rejectService } = useMutationWithHandler(
        { mutationFn: rejectedService },
        {
            reactQueryKeys: QUERY_KEYS.SERVICE,
            successMessage: t('rejected-service-success'),
            onSuccess: () => refetch(),
        }
    )

    // Row actions
    const rowActions = useMemo<DataTableAction<Service>[]>(
        () => [
            {
                key: 'confirm',
                label: t('confirm-service'),
                icon: <CheckIcons className="w-4 h-4" />,
                variant: 'success',
                hidden: (row) => row?.service_status !== 'waiting-confirm',
                onClick: (row) => {
                    setConfirmation({
                        open: true,
                        title: t('confirm-service'),
                        description: t('confirm-service-warning'),
                        onConfirm: () => confirm(row?.service_id),
                    })
                },
            },
            {
                key: 'close-poc',
                label: t('closed-service-poc'),
                icon: <CheckIcons className="w-4 h-4" />,
                variant: 'warning',
                hidden: (row) => row?.service_status !== 'trialing',
                onClick: (row) => {
                    setModal('ServiceModals/ResponsePOC/index', {
                        service: row,
                        serviceID: row?.service_id,
                    })
                },
            },
            {
                key: 'retention-poc',
                label: t('retention-service-poc'),
                icon: <CheckIcons className="w-4 h-4" />,
                variant: 'success',
                hidden: (row) => row?.service_status !== 'trialing',
                onClick: (row) => {
                    setModal('ServiceModals/RetentionPOC/index', {
                        service: row,
                        serviceID: row?.service_id,
                    })
                },
            },
            {
                key: 'reject',
                label: t('service-rejected'),
                icon: <CancelIcons className="w-4 h-4" />,
                variant: 'danger',
                hidden: (row) => row?.service_status !== 'waiting-confirm',
                onClick: (row) => {
                    setConfirmation({
                        open: true,
                        useReason: true,
                        title: t('rejected-service'),
                        description: t('rejected-service-warning'),
                        onConfirm: (reason) => {
                            rejectService({
                                serviceID: row?.service_id,
                                reason: reason as string,
                            })
                        },
                    })
                },
            },
        ],
        [t, confirm, rejectService, setConfirmation, setModal]
    )

    return (
        <Paper>
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
            />

            {stateComponent || (
                <DataTableCustomized
                    columns={columns}
                    rows={services}
                    rowKey={(row) => String(row?.service_id)}
                    size="small"
                    resizable
                    loading={!services.length && !pagination}
                    isFetching={isFetching}
                    emptyMessage={t('no-services')}
                    actions={rowActions}
                    sorting={{
                        sortBy: params.sort_by,
                        sortOrder: params.sort_order,
                        onSortChange: handleSortChange,
                    }}
                    pagination={{
                        page: params.page || 1,
                        pageSize: params.page_size || 20,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        pageSizeOptions: [10, 20, 50, 100],
                    }}
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search-service'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'services-poc-list',
                        },
                    }}
                />
            )}
        </Paper>
    )
}

export default POC
